import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import MultipleLineAndBarChart from "../../components/charts/MultipleLineAndBarChart";
import { formattedWasteColor } from "../../components/formatted/FormattedText";
import { getWastecategoryList } from "../../services/waste";
import DashboardFluxCompositionBar from "./DashboardFluxCompositionBar";
import {
  buildSortingErrorRateByFluxSeries,
  buildSortingErrorRateComposition,
  formatChartMonthTitle,
  formatRate,
} from "./dashboardKpiUtils";

const MIN_TIME_BUCKETS_FOR_HISTOGRAM = 3;

const WASTE_CATEGORY_LIST_PARAMS = {
  keywordFilter: "",
  listUnCodeFullFilter: {},
  listUnCodeEmptyFilter: {},
  listWasteCodeFullFilter: {},
  listWasteCodeEmptyFilter: {},
  sortByField: "label",
  orderSortByField: "1",
};

const resolveFluxColor = (wasteCategories, dataset) => {
  const match = wasteCategories.find((item) => item.label === dataset.label);
  return match?.displayColor || dataset.backgroundColor;
};

const DashboardSortingErrorChart = ({
  dataResult,
  collectionDataResult = {},
}) => {
  const { t, i18n } = useTranslation();
  const [wasteCategories, setWasteCategories] = useState([]);
  const { dataSetsSortingErrorByFluxChart, labelsByTimeChart } = useMemo(() => {
    const { series, labels } = buildSortingErrorRateByFluxSeries(
      dataResult,
      collectionDataResult,
    );
    return {
      dataSetsSortingErrorByFluxChart: series.map((item) => ({
        label: item.flux,
        data: item.data.map((point) => ({
          x: formatChartMonthTitle(point.x, i18n.language),
          y: point.y,
          z: item.flux,
        })),
        backgroundColor: formattedWasteColor(item.flux),
        type: "bar",
      })),
      labelsByTimeChart: labels.map((monthKey) =>
        formatChartMonthTitle(monthKey, i18n.language),
      ),
    };
  }, [collectionDataResult, dataResult, i18n.language]);

  const useHistogram =
    labelsByTimeChart.length >= MIN_TIME_BUCKETS_FOR_HISTOGRAM;
  const segments = useMemo(() => {
    const rates = buildSortingErrorRateComposition(
      dataResult,
      collectionDataResult,
    );
    return rates.map((segment) => ({
      ...segment,
      color: resolveFluxColor(wasteCategories, {
        label: segment.label,
        backgroundColor: formattedWasteColor(segment.label),
      }),
    }));
  }, [collectionDataResult, dataResult, wasteCategories]);

  useEffect(() => {
    if (useHistogram) return undefined;
    let cancelled = false;
    getWastecategoryList(WASTE_CATEGORY_LIST_PARAMS)
      .then((response) => {
        if (!cancelled) setWasteCategories(response.data.data || []);
      })
      .catch(() => {
        if (!cancelled) setWasteCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [useHistogram]);

  return (
    <article className="dashboard-chart-card">
      <div className="dashboard-chart-heading">
        <h4 className="dashboard-chart-title">
          {t("Sorting error by waste category")}
        </h4>
        <p className="dashboard-chart-subtitle">
          {useHistogram
            ? t("dashboard.sortingErrorsChartHistogramSubtitle")
            : t("dashboard.sortingErrorsChartCompositionSubtitle")}
        </p>
      </div>
      {useHistogram ? (
        <div className="dashboard-chart-canvas">
          <MultipleLineAndBarChart
            stackedChart={false}
            type="bar"
            borderWidthChart={1}
            labelsChart={labelsByTimeChart}
            aspectRatioChart={2.4}
            datasetsChart={dataSetsSortingErrorByFluxChart}
            indexAxisChart="x"
            maintainAspectRatioChart={false}
            displayGrid
            displayTicks
            xAxisType="category"
            reverseChart={false}
            displayLegend
            displayTooltip
            messageLabelTooltip={t("dashboard.sortingErrorsChartTooltip")}
            unitValueChart=""
            maxYAxis={null}
            maxBarThicknessChart={100}
            categoryPercentageChart={0.7}
          />
        </div>
      ) : (
        <DashboardFluxCompositionBar
          segments={segments}
          emptyLabel={t("No sorting errors detected")}
          formatValue={formatRate}
        />
      )}
    </article>
  );
};

export default DashboardSortingErrorChart;
